import { useEffect, useState, useRef } from 'react'
import { useForm } from './form-provider'
import { EDUCATION_QUALIFICATIONS } from './lib/constants'
import { countriesList } from './lib/countriesList'
import axios from 'axios'
import { useUser } from '../utils/auth'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

const Input = ({ id, label, value, onChange, type = "text", error }) => (
  <div className="relative">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
        error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
      }`}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const Select = ({ label, value, onChange, options, placeholder, isLoading }) => (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="" disabled>{isLoading ? "Loading..." : placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 top-6 flex items-center pr-2 pointer-events-none">
      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </div>
  </div>
);

const SearchableSelect = ({ label, value, onChange, options, placeholder, isLoading, onOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setDisplayValue(value || '');
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputClick = () => {
    setIsOpen(true);
    if (onOpen) onOpen();
  };

  const handleOptionSelect = (option) => {
    onChange(option);
    setDisplayValue(option);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={handleInputClick}
          placeholder={isLoading ? "Loading..." : placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-300 max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleOptionSelect(option)}
              >
                {option}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

const OtpInput = ({ otp, setOtp, maxLength }) => {
  const inputs = useRef([]);

  useEffect(() => {
    if (inputs.current[0]) {
      inputs.current[0].focus();
    }
  }, []);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    // Allow only one input
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input if current field is filled
    if (value && index < maxLength - 1) {
      inputs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Move to previous input if backspace is pressed
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();
    
    // Check if pasted data contains only numbers
    if (!/^\d+$/.test(pastedData)) return;
    
    const otpArray = pastedData.substring(0, maxLength).split("");
    
    if (otpArray.length > 0) {
      setOtp(otpArray.concat(Array(maxLength - otpArray.length).fill("")));
      
      if (otpArray.length < maxLength) {
        inputs.current[otpArray.length].focus();
      } else {
        inputs.current[maxLength - 1].focus();
      }
    }
  };

  return (
    <div className="flex justify-between items-center space-x-2">
      {Array.from({ length: maxLength }, (_, index) => (
        <input
          key={index}
          type="text"
          ref={(el) => (inputs.current[index] = el)}
          value={otp[index] || ""}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className="w-10 h-12 text-center border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={1}
        />
      ))}
    </div>
  );
};

export function StepOne() {
  const { formData, updateFormData, setStep } = useForm();
  const { user, isLoading: isAuthLoading } = useUser();
  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(formData?.phoneVerified || false);
  
  // OTP related states
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");

  // Use user data from auth context
  useEffect(() => {
    if (user && !isAuthLoading) {
      // set initial data
      setInitialData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || '',
        currentEducation: user.currentEducation || '',
        educationCountry: user.educationCountry || ''
      });
    }
  }, [user, isAuthLoading]);

  useEffect(() => {
    if (initialData) {
      updateFormData(initialData);
      setIsPhoneVerified(true);
    }
  }, [initialData]);

  const fetchCountries = () => {
    if (countries.length === 0 && !isLoadingCountries) {
      setIsLoadingCountries(true);
      const countryNames = countriesList.sort();
      setCountries(countryNames);
      setIsLoadingCountries(false);
    }
  };

  const validateFields = (field, value) => {
    let newErrors = { ...errors };
  
    if (field === "name" && !value) newErrors.name = "Name is required.";
    else if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      newErrors.email = "Invalid email address.";
    else if (field === "phone" && value.length < 8)
      newErrors.phone = "Enter a valid phone number.";
    else if (field === "currentEducation" && !value)
      newErrors.currentEducation = "Required.";
    else if (field === "educationCountry" && !value)
      newErrors.educationCountry = "Required.";
    else delete newErrors[field];
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    if (isSubmitting) return; // Prevent changes while submitting
    
    // Reset verification status if phone changes
    if (field === "phone" && isPhoneVerified && value !== initialData?.phone) {
      updateFormData({ phoneVerified: false });
      setIsPhoneVerified(false);
      setShowOtp(false);
      setIsOtpSent(false);
    }
    
    updateFormData({ [field]: value });
    validateFields(field, value);
  };

  useEffect(() => {
    // if phone number has 10 length then check verification status
    if (formData.phone.length >= 10) {
      checkVerificationStatus(formData.phone);
    }
  }, [formData.phone]);

  const checkVerificationStatus = async (phone) => {
    try {
      // Pass the complete phone number with country code
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/otp/check-verified`,
        { params: { phone } }
      );
      
      if (data.verified) {
        setIsPhoneVerified(true);
        // If the phone is already verified, update the form
        updateFormData({ 
          phoneVerified: true,
          // Update other fields if available from stored data
          ...(data.userData?.name && { name: data.userData.name }),
          ...(data.userData?.email && { email: data.userData.email })
        });
        
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phone;
          return newErrors;
        });
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      // Continue without verification if there's an error
    }
  };

  const handleSendOtp = async () => {
    // Validate phone number
    if (!validateFields("phone", formData.phone)) return;
    
    setIsSendingOtp(true);
    setOtpError("");
    
    try {
      // Use the phone number with country code directly
      const mobileWithCode = formData.phone;
      
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/otp/send`, {
        mobile: mobileWithCode
      });
      
      if (data && data.type === "success") {
        setIsOtpSent(true);
        setShowOtp(true);
        setOtp(Array(6).fill(""));
      } else {
        setOtpError("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP send error:", error);
      setOtpError("Failed to send OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setIsSendingOtp(true);
    setOtpError("");
    
    try {
      const mobileWithCode = formData.phone;
      
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/otp/resend`, {
        mobile: mobileWithCode,
        retrytype: 'text'
      });
      
      if (data && data.type === "success") {
        setOtp(Array(6).fill(""));
      } else {
        setOtpError("Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP resend error:", error);
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };
  
  const handleVerifyOtp = async () => {
    // Check if OTP is complete
    if (otp.some(digit => digit === "")) {
      setOtpError("Please enter the complete OTP");
      return;
    }
    
    setIsVerifyingOtp(true);
    setOtpError("");
    
    try {
      const mobileWithCode = formData.phone;
      const otpString = otp.join("");
      
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/otp/verify`, {
        mobile: mobileWithCode,
        otp: otpString,
        name: formData.name,
        email: formData.email
      });
      
      if (data && data.type === "success") {
        updateFormData({ phoneVerified: true });
        setIsPhoneVerified(true);
        setShowOtp(false);
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleNext = async () => {
    // Check if phone is verified
    if (!isPhoneVerified) {
      setErrors(prev => ({ ...prev, phone: "Please verify your phone number first." }));
      return;
    }
    
    const isValid = Object.keys(formData).every((field) =>
      field === "phoneVerified" ? true : validateFields(field, formData[field])
    );

    if (!isValid) return;
    setStep(2);
  }

  return (
    <div className="space-y-6">
      {isAuthLoading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <>
          <Input
            id="name"
            label="Your Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            error={errors.name}
            disabled={isSubmitting}
          />
          <Input
            id="email"
            type="email"
            label="Email Address"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={errors.email}
            disabled={isSubmitting}
          />
          
          <div className="space-y-2">
            <div className="flex flex-col md:flex-row md:space-x-2 relative">
              <div className="flex-grow">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="phone-input-container">
                  <PhoneInput
                    country={'in'}
                    value={formData.phone}
                    onChange={(value) => handleChange("phone", value)}
                    inputProps={{
                      id: "phone",
                      required: true,
                      className: 'w-full py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-12',
                    }}
                    containerClass="w-full phone-input-container"
                    buttonStyle={{ background: 'transparent', border: 'none' }}
                    isValid={(value) => {
                      if (value.length < 10) return false;
                      return true;
                    }}
                    disabled={isSubmitting} // Removed isPhoneVerified to make it always editable
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <style jsx="true">{`
                  .phone-input-container .react-tel-input .selected-flag {
                    padding: 0 6px 0 8px;
                    background: transparent !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  }
                  .phone-input-container .react-tel-input .selected-flag:hover, 
                  .phone-input-container .react-tel-input .selected-flag:focus,
                  .phone-input-container .react-tel-input .selected-flag.open {
                    background: transparent !important;
                  }
                  .phone-input-container .react-tel-input .selected-flag .flag {
                    position: static;
                    transform: none;
                    margin: auto;
                  }
                  .phone-input-container .react-tel-input .form-control {
                    padding-left: 48px;
                    width: 100%;
                  }
                `}</style>
              </div>
              
              {isPhoneVerified && (
                <div className="absolute text-sm right-0 top-0 flex items-center text-green-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Verified</span>
                </div>
              )}
              
              {!isPhoneVerified && (
                <button
                  onClick={handleSendOtp}
                  disabled={!formData.phone || errors.phone || isSendingOtp || isSubmitting}
                  className="px-4 py-2 mt-2 md:mt-6 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed h-10"
                >
                  {isSendingOtp ? "Sending..." : isOtpSent ? "Resend OTP" : "Send OTP"}
                </button>
              )}
            </div>
            
            {showOtp && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Enter verification code
                </label>

                <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 justify-between">
                  <OtpInput otp={otp} setOtp={setOtp} maxLength={6} />
                  
                  <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:items-center">
                    <button
                      onClick={handleVerifyOtp}
                      disabled={otp.some(digit => digit === "") || isVerifyingOtp}
                      className="px-4 py-3 text-white text-sm bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
                    </button>
                    
                    <button
                      onClick={handleResendOtp}
                      disabled={isSendingOtp}
                      className="text-blue-600 pb-2 text-sm hover:underline focus:outline-none"
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
                {otpError && <p className="text-red-500 text-xs mt-1">{otpError}</p>}
              </div>
            )}
          </div>
          
          <Select
            label="Current Education Qualification"
            value={formData.currentEducation}
            onChange={(value) => handleChange("currentEducation", value)}
            options={EDUCATION_QUALIFICATIONS}
            placeholder="Select qualification"
            disabled={isSubmitting} 
          />
          <SearchableSelect
            label="Education Country"
            value={formData.educationCountry}
            onChange={(value) => handleChange("educationCountry", value)}
            options={countries}
            placeholder="Select country"
            isLoading={isLoadingCountries}
            onOpen={fetchCountries}
            disabled={isSubmitting} 
          />
          <button
            onClick={handleNext}
            disabled={Object.keys(errors).length > 0 || isSubmitting || !isPhoneVerified}
            className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Processing..." : "Next"} 
          </button>
        </>
      )}
    </div>
  );
}